import React, { memo, useState, useRef, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import { MapPin, Compass, FileText, Sparkles, ArrowRight, Zap, AlertCircle } from 'lucide-react';

export interface SectionNodeData {
  sectionTitle?: string;
  matchRoute?: string;
  sectionPersonaOverride?: string;
  [key: string]: unknown;
}

export const SectionNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as SectionNodeData;
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);

  const { isTenantView, authToken, tenantApiKey, pulseState, openPortalData } = useTenantView();

  const persistNodeDataDirectly = (field: keyof SectionNodeData, value: any, allUpdates?: Partial<SectionNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          [field]: value,
          ...(allUpdates || {})
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (tenantApiKey) {
          headers['x-tenant-api-key'] = tenantApiKey;
        }

        const res = await fetch('/api/admin/luna-flows/save-node-data', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'SECTION_NODE',
            data: payloadData,
            widgetData: payloadData,
          }),
        });

        if (res.ok) {
          setSaveIndicator('✅ Saved to DB');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.warn('[SectionNode] Direct persist warning:', err);
      }
    }, 600);
  };

  const handleChange = (field: keyof SectionNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    persistNodeDataDirectly(field, value);
  };

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);

  return (
    <div
      className={`rounded-2xl border transition-all duration-300 w-[380px] select-none group relative backdrop-blur-xl bg-gradient-to-b from-[#0F0E28]/98 via-[#09081B]/98 to-[#04040E]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-indigo-400 ring-2 ring-indigo-400/50 shadow-[0_0_35px_rgba(99,102,241,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : isWired
          ? 'border-indigo-500/40 hover:border-indigo-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(99,102,241,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* ── Floating Cyber-HUD Quick Jump Toolbar ── */}
      <div className="absolute -top-11 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-auto z-50 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-slate-950/95 backdrop-blur-xl border border-indigo-500/40 shadow-[0_0_25px_rgba(99,102,241,0.4)]">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openPortalData?.('section', id);
          }}
          className="nodrag flex items-center gap-1.5 text-[11px] font-bold text-indigo-300 hover:text-indigo-200 transition-colors cursor-pointer"
        >
          <Compass size={13} className="text-indigo-400 animate-pulse" />
          <span>Section Studio</span>
          <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-indigo-500/20 text-indigo-300 font-mono border border-indigo-500/30">
            HUD ↗
          </span>
        </button>
      </div>

      {/* ── Left Side: Multi-Port Input Handles for Section-Scoped Nodes ── */}
      {/* 1. Form Generator Input Port */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-section-form"
        style={{ top: '30%' }}
        className="!bg-cyan-400 !w-4 !h-4 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(6,182,212,1)]"
        title="Input: Section GenUI Form Generator"
      />
      {/* 2. Knowledge Base Input Port */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-section-knowledge"
        style={{ top: '70%' }}
        className="!bg-emerald-400 !w-4 !h-4 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(16,185,129,1)]"
        title="Input: Section Knowledge Vault"
      />
      {/* Universal Target Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-section"
        style={{ top: '50%' }}
        className="!bg-indigo-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-125 z-20 shadow-[0_0_10px_rgba(99,102,241,1)]"
        title="Universal Section Input"
      />

      {/* ── Right Side: Output Handle to Luna Core Hub ── */}
      <Handle
        type="source"
        position={Position.Right}
        id="out-section"
        className="!bg-indigo-400 !w-4 !h-4 !border-2 !border-slate-950 !-right-2 top-1/2 -translate-y-1/2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(99,102,241,1)]"
        title="Output to Luna Core Hub (Route Trigger)"
      />

      <Handle
        type="target"
        position={Position.Top}
        id="in-top"
        className="!bg-indigo-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-top-2 transition-transform hover:scale-125 shadow-[0_0_8px_rgba(99,102,241,0.8)]"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="out-bottom"
        className="!bg-indigo-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-bottom-2 transition-transform hover:scale-125 shadow-[0_0_8px_rgba(99,102,241,0.8)]"
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-950/80 via-purple-950/60 to-slate-950/80 px-4 py-3 border-b border-indigo-500/30 flex items-center justify-between rounded-t-2xl">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-indigo-500/20 border border-indigo-500/40 text-indigo-300 shadow-[0_0_12px_rgba(99,102,241,0.3)]">
            <Compass size={16} />
          </div>
          <div>
            <h4 className="text-xs font-black text-slate-100 tracking-wide">Section & Route Node</h4>
            <p className="text-[10px] text-indigo-300/70 font-mono">Context-Aware Route Switcher</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {saveIndicator && (
            <span className="text-[9px] font-mono font-bold text-indigo-400 animate-pulse bg-indigo-500/10 px-2 py-0.5 rounded-full border border-indigo-500/30">
              {saveIndicator}
            </span>
          )}
          <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
            ROUTE
          </span>
        </div>
      </div>

      {/* Wiring Live Status Bar */}
      <div className="mx-4 mt-3">
        {isWired ? (
          <div className="px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <Zap size={11} className="text-emerald-400" /> Wired to Luna Core Hub
            </span>
            <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-wide">ACTIVE</span>
          </div>
        ) : (
          <div className="px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <AlertCircle size={11} className="text-amber-400" /> Disconnected
            </span>
            <span className="text-[9px] text-amber-400/80">Connect to Luna Core Hub</span>
          </div>
        )}
      </div>

      <div className="p-4 space-y-3.5 text-xs">
        <div>
          <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5">
            <FileText size={12} className="text-indigo-400" /> Section Name / Label
          </label>
          <input
            type="text"
            className="nodrag nowheel w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400/30 transition-all placeholder:text-slate-600 font-bold"
            placeholder="e.g. Tax Filing Section, Banking Portal"
            value={nodeData.sectionTitle || ''}
            onChange={(e) => handleChange('sectionTitle', e.target.value)}
          />
        </div>

        <div>
          <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5">
            <MapPin size={12} className="text-indigo-400" /> Target Route / URL Path / Hash
          </label>
          <input
            type="text"
            className="nodrag nowheel w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400/30 transition-all placeholder:text-slate-600 font-mono font-bold text-indigo-200"
            placeholder="e.g. /tax, /tax-filing, #tax, or /banking"
            value={nodeData.matchRoute || ''}
            onChange={(e) => handleChange('matchRoute', e.target.value)}
          />
        </div>

        <div>
          <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5">
            <Sparkles size={12} className="text-indigo-400" /> Section Persona Override (Optional)
          </label>
          <textarea
            rows={3}
            className="nodrag nowheel w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400/30 transition-all placeholder:text-slate-600 resize-none font-sans leading-relaxed"
            placeholder="e.g. You are now Luna acting as a Senior Tax Advisor. Greet the visitor about their annual tax return..."
            value={nodeData.sectionPersonaOverride || ''}
            onChange={(e) => handleChange('sectionPersonaOverride', e.target.value)}
          />
        </div>

        {/* Port Guidance Note */}
        <div className="p-2.5 rounded-xl bg-indigo-950/30 border border-indigo-500/20 text-[10px] text-slate-300 space-y-1">
          <div className="font-bold text-indigo-300 flex items-center gap-1">
            <ArrowRight size={11} /> Context Switching Rule:
          </div>
          <p className="text-slate-400 leading-snug">
            Wire Form Generators & Knowledge Vaults directly to this section's left ports to isolate them to this specific route.
          </p>
        </div>

      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        id="out-bottom"
        className="!bg-indigo-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-bottom-2 transition-transform hover:scale-125"
      />
    </div>
  );
});

SectionNode.displayName = 'SectionNode';
export default SectionNode;
